import React, { useState } from "react";

/**
 * Small inline-SVG chart set for the admin dashboard.
 *
 * No charting dependency is installed and these are simple magnitude plots, so
 * they are drawn directly. Colors come from the validated palette (see
 * Analytics.scss): a single blue hue carries magnitude, because identity here
 * is carried by the axis label, not by color — coloring bars by rank would
 * repaint them whenever a filter changes the ordering.
 */

export const SERIES = "#3987e5";
export const SERIES_ALT = "#d95926";
export const STATUS = {
  good: "#0ca30c",
  warning: "#fab219",
  serious: "#ec835a",
  critical: "#d03b3b",
};

const AXIS = "#4a4a5e";
const GRID = "#26263a";
const INK_MUTED = "#8b8ba0";

export function formatBucket(iso, granularity) {
  const d = new Date(iso);
  const month = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  const day = d.getUTCDate();
  if (granularity === "month") return `${month} ${String(d.getUTCFullYear()).slice(2)}`;
  return `${month} ${day}`;
}

export function formatNumber(n) {
  if (n == null || isNaN(n)) return "—";
  return Number(n).toLocaleString("en-US");
}

export function formatMoney(n) {
  if (n == null || isNaN(n)) return "—";
  return `$${Number(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Time-series column chart. One series, so no legend — the title names it.
 */
export function TimeSeriesChart({
  data,
  granularity,
  label,
  height = 190,
  dense = false,
}) {
  // The SVG scales to its container, so viewBox font sizes shrink with it. A
  // half-width chart needs proportionally larger type to stay legible.
  const fs = dense ? 17 : 11;
  const [hover, setHover] = useState(null);

  if (!data || !data.length) {
    return <div className="chart-empty">No data in this range.</div>;
  }

  const max = Math.max(...data.map((d) => d.count), 1);
  const padL = dense ? 62 : 44;
  const padR = 8;
  const padT = 12;
  const padB = dense ? 38 : 28;
  const innerW = 1000 - padL - padR;
  const innerH = height - padT - padB;

  const slot = innerW / data.length;
  const barW = Math.max(2, Math.min(38, slot - 2)); // 2px surface gap
  const radius = Math.min(4, barW / 2);

  // Four recessive gridlines, rounded to readable values.
  const ticks = 4;
  const tickVals = Array.from({ length: ticks + 1 }, (_, i) =>
    Math.round((max / ticks) * i)
  );

  // Roughly six x labels regardless of bucket count.
  const labelEvery = Math.max(1, Math.ceil(data.length / (dense ? 4 : 6)));

  const total = data.reduce((a, b) => a + b.count, 0);

  return (
    <div className="chart-wrap">
      <svg
        viewBox={`0 0 1000 ${height}`}
        className="chart-svg"
        role="img"
        aria-label={`${label}: ${total} total across ${data.length} ${granularity} buckets`}
        onMouseLeave={() => setHover(null)}
      >
        {tickVals.map((v, i) => {
          const y = padT + innerH - (v / max) * innerH;
          return (
            <g key={i}>
              <line x1={padL} x2={padL + innerW} y1={y} y2={y} stroke={GRID} strokeWidth="1" />
              <text
                x={padL - 8}
                y={y + fs / 3}
                textAnchor="end"
                fill={INK_MUTED}
                fontSize={fs}
              >
                {v}
              </text>
            </g>
          );
        })}

        <line
          x1={padL}
          x2={padL + innerW}
          y1={padT + innerH}
          y2={padT + innerH}
          stroke={AXIS}
          strokeWidth="1"
        />

        {data.map((d, i) => {
          const h = max ? (d.count / max) * innerH : 0;
          const x = padL + i * slot + (slot - barW) / 2;
          const y = padT + innerH - h;
          const isHover = hover === i;
          return (
            <g key={d.bucket}>
              {/* Hit target is the full column, not just the bar. */}
              <rect
                x={padL + i * slot}
                y={padT}
                width={slot}
                height={innerH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
              />
              {d.count > 0 && (
                <rect
                  x={x}
                  y={y}
                  width={barW}
                  height={Math.max(h, 2)}
                  rx={radius}
                  ry={radius}
                  fill={SERIES}
                  opacity={hover == null || isHover ? 1 : 0.55}
                  pointerEvents="none"
                />
              )}
            </g>
          );
        })}

        {data.map((d, i) =>
          i % labelEvery === 0 ? (
            <text
              key={`l-${d.bucket}`}
              x={padL + i * slot + slot / 2}
              y={height - 8}
              textAnchor="middle"
              fill={INK_MUTED}
              fontSize={fs}
            >
              {formatBucket(d.bucket, granularity)}
            </text>
          ) : null
        )}
      </svg>

      {hover != null && data[hover] && (
        <div className="chart-tooltip">
          <strong>{formatNumber(data[hover].count)}</strong> {label}
          <span className="chart-tooltip-sub">
            {formatBucket(data[hover].bucket, granularity)}
            {granularity === "week" ? " (week of)" : ""}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Ranked horizontal bars for categorical magnitude (packages, sources, types).
 * Single hue on purpose — see the note at the top of this file.
 */
export function RankedBars({
  rows,
  valueKey = "count",
  labelKey = "label",
  note,
  capitalizeLabels = false,
}) {
  if (!rows || !rows.length) {
    return <div className="chart-empty">No data.</div>;
  }
  const max = Math.max(...rows.map((r) => r[valueKey] || 0), 1);
  const total = rows.reduce((a, r) => a + (r[valueKey] || 0), 0);

  return (
    <div className="ranked-bars">
      {rows.map((r) => {
        const v = r[valueKey] || 0;
        const pct = total ? (v / total) * 100 : 0;
        return (
          <div className="ranked-row" key={r[labelKey]}>
            <div
              className={`ranked-label ${capitalizeLabels ? "capitalize" : ""}`}
              title={r[labelKey]}
            >
              {r[labelKey]}
            </div>
            <div className="ranked-track">
              <div
                className="ranked-fill"
                style={{ width: `${(v / max) * 100}%`, background: SERIES }}
              />
            </div>
            <div className="ranked-value">
              {formatNumber(v)}
              <span className="ranked-pct">{pct.toFixed(1)}%</span>
            </div>
            {r.sub ? <div className="ranked-sub">{r.sub}</div> : null}
          </div>
        );
      })}
      {note ? <div className="chart-note">{note}</div> : null}
    </div>
  );
}

/**
 * A KPI tile. `definition` is required — every number on this dashboard has to
 * say what it measures.
 */
export function StatTile({
  value,
  label,
  definition,
  tone = "neutral",
  sub,
  emphasis = false,
}) {
  return (
    <div className={`analytics-card tone-${tone} ${emphasis ? "emphasis" : ""}`}>
      <div className="card-value">{value}</div>
      <div className="card-label">
        {label}
        {definition ? (
          <span className="card-info" tabIndex={0} aria-label={definition}>
            ?<span className="card-tip">{definition}</span>
          </span>
        ) : null}
      </div>
      {sub ? <div className="card-sub">{sub}</div> : null}
    </div>
  );
}
